import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import ExcelJS from "exceljs";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    RadioGroup as ZhRadioGroup,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { DATETIME_PICKER_FORMAT, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { CANCEL_TYPE } from "@/meta/logistics/goodsAndShipAnalysis";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import { confirmDialog } from "@/utils/confirmDialog";
import { calcRangeByScope } from "./utils";
import { ShipNeedConversionFunnel } from "./components/ShipNeedConversionFunnel";
import { ShipNeedExposureAnalysis } from "./components/ShipNeedExposureAnalysis";

export interface ShipNeedAnalysisQuery {
    page?: number;
    size?: number;
    platConvertType?: number;
    scope?: number;
    beginTime?: string;
    endTime?: string;
}

export interface ShipNeedAnalysisCount {
    totalNum?: number;
    waitNum?: number;
    cancelNum?: number;
    closeNum?: number;
    quoteNum?: number;
    generateNum?: number;
}

export interface ShipNeedAnalysisPageProps {
    loadCount?: (
        query: Pick<ShipNeedAnalysisQuery, "scope" | "beginTime" | "endTime">
    ) => Promise<ShipNeedAnalysisCount>;
    loadDetails?: (query: ShipNeedAnalysisQuery) => Promise<Page<Record<string, unknown>>>;
    loadExportDetails?: (query: ShipNeedAnalysisQuery) => Promise<Page<Record<string, unknown>>>;
    downloadDetails?: (rows: Record<string, unknown>[], fileName: string) => Promise<unknown>;
    permissions?: string[];
}

/** Vue 船运需求转化统计 API adapter。 */
export const loadShipNeedAnalysisCount = (query: Pick<ShipNeedAnalysisQuery, "scope" | "beginTime" | "endTime">) =>
    import("@/api/services/shipNeddTransform").then(({ shipNeedTransform }) =>
        shipNeedTransform.getAnalysisRequirementConversionCount(query)
    );

/** Vue 船运需求状态明细 API adapter。 */
export const loadShipNeedAnalysisDetails = (query: ShipNeedAnalysisQuery) =>
    import("@/api/services/shipNeddTransform").then(
        ({ shipNeedTransform }) =>
            shipNeedTransform.getAnalysisRequirementConversionDetail(query) as Promise<Page<Record<string, unknown>>>
    );

const statuses = [
    { id: 1, label: "待发布需求数", field: "waitNum" as const },
    { id: 2, label: "已取消需求数", field: "cancelNum" as const },
    { id: 4, label: "被报价需求数", field: "quoteNum" as const },
    { id: 5, label: "生成运单数", field: "generateNum" as const }
];

const textValue = (value: unknown) => (value == null || value === "" ? "--" : String(value));

const objectValue = (value: unknown): Record<string, unknown> =>
    typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

const requirementValue = (row: Record<string, unknown>) => objectValue(row.shippingRequirementPlat);

const transportOrderValue = (row: Record<string, unknown>) => objectValue(row.transportOrderShip);

const formatDetailDate = (value: unknown) =>
    value == null || value === "" ? "--" : dateFormat(String(value), DATETIME_PICKER_FORMAT);

const enumValue = (value: unknown, getIntro: (code: number) => string | null | undefined) =>
    value == null || value === "" ? "--" : getIntro(Number(value)) || "--";

/** 旧 Vue 主明细导出文件名，状态值与接口 platConvertType 保持一致。 */
const detailExportFileNames: Record<number, string> = {
    1: "待发布需求明细",
    2: "已取消需求明细",
    4: "被报价需求明细",
    5: "生成运单明细"
};

/** 将主明细行转换为旧 Vue 导出表格的闭合集合字段。 */
const getDetailExportRow = (row: Record<string, unknown>, status: number, index: number) => {
    const requirement = requirementValue(row);
    const transportOrder = transportOrderValue(row);
    const owner = objectValue(requirement.ownerEnterprise);
    switch (status) {
        case 1:
            return [
                index + 1,
                requirement.id || "",
                requirement.handlerName || "",
                owner.name || owner.enterpriseName || "--",
                requirement.mobile || ""
            ];
        case 2:
            return [
                index + 1,
                requirement.id || "--",
                CANCEL_TYPE.getIntroByCode(Number(requirement.cancelType)) || "--",
                requirement.cancelReasonContent || "--",
                requirement.operator || "--",
                formatDetailDate(requirement.cancelTime),
                requirement.ownerCancelRemark || "--"
            ];
        case 4:
            return [index + 1, requirement.id || "", row.acceptCount || 0, formatDetailDate(row.newestAcceptTime)];
        case 5:
            return [
                index + 1,
                requirement.id || "",
                transportOrder.id || "",
                formatDetailDate(transportOrder.createdTime),
                WAYBILL_STATE.getIntroByCode(Number(transportOrder.state)) || "--"
            ];
        default:
            return [index + 1, requirement.id || ""];
    }
};

/** 生成并下载旧 Vue 主明细 Excel，保留当前状态对应的列顺序。 */
async function downloadShipNeedDetails(rows: Record<string, unknown>[], fileName: string, status: number) {
    const headersByStatus: Record<number, string[]> = {
        1: ["编号", "需求编号", "船务专员", "货主名称", "货主手机号"],
        2: ["编号", "需求编号", "取消类型", "取消原因", "操作人", "取消时间", "补充说明"],
        4: ["编号", "需求编号", "报价数量", "最新报价时间"],
        5: ["编号", "需求编号", "运单编号", "生成运单时间", "运单当前状态"]
    };
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("需求明细");
    worksheet.addRow(headersByStatus[status] || ["编号", "需求编号"]);
    rows.forEach((row, index) => worksheet.addRow(getDetailExportRow(row, status, index)));
    const buffer = await workbook.xlsx.writeBuffer();
    const link = document.createElement("a");
    link.download = `${fileName}.xlsx`;
    link.href = window.URL.createObjectURL(new Blob([buffer]));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
}

/** 复用旧 Vue 详情路由，保留来源标记用于返回分析页。 */
const requirementLink = (row: Record<string, unknown>) => {
    const id = requirementValue(row).id;
    return id ? (
        <Link to={`/logistics/shippingNeed/platformShipNeedInfo/${id}?from=shipNeedAnalysis`}>{String(id)}</Link>
    ) : (
        textValue(id)
    );
};

/** 旧 Vue 按当前状态切换明细列，React 仍使用 zhihao-ui-react Table 展示。 */
const detailColumnsByStatus: Record<number, TableColumnProps<Record<string, unknown>>[]> = {
    1: [
        { label: "编号", width: 80, render: ({ rowIndex }) => rowIndex + 1 },
        { label: "需求编号", render: ({ row }) => requirementLink(row) },
        { label: "船务专员", render: ({ row }) => textValue(requirementValue(row).handlerName) },
        {
            label: "货主名称",
            render: ({ row }) =>
                textValue(
                    objectValue(requirementValue(row).ownerEnterprise).name ||
                        objectValue(requirementValue(row).ownerEnterprise).realName
                )
        },
        { label: "货主手机号", render: ({ row }) => textValue(requirementValue(row).mobile) },
        { label: "需求创建时间", render: ({ row }) => formatDetailDate(requirementValue(row).createdTime) }
    ],
    2: [
        { label: "编号", width: 80, render: ({ rowIndex }) => rowIndex + 1 },
        { label: "需求编号", render: ({ row }) => requirementLink(row) },
        {
            label: "取消类型",
            render: ({ row }) => enumValue(requirementValue(row).cancelType, code => CANCEL_TYPE.getIntroByCode(code))
        },
        { label: "取消原因", render: ({ row }) => textValue(requirementValue(row).cancelReasonContent) },
        { label: "操作人", render: ({ row }) => textValue(requirementValue(row).operator) },
        { label: "取消时间", render: ({ row }) => formatDetailDate(requirementValue(row).cancelTime) },
        { label: "补充说明", render: ({ row }) => textValue(requirementValue(row).ownerCancelRemark) },
        { label: "需求创建时间", render: ({ row }) => formatDetailDate(requirementValue(row).createdTime) }
    ],
    4: [
        { label: "编号", width: 80, render: ({ rowIndex }) => rowIndex + 1 },
        { label: "需求编号", render: ({ row }) => requirementLink(row) },
        { label: "报价数量", render: ({ row }) => textValue(row.acceptCount) },
        { label: "最新报价时间", render: ({ row }) => formatDetailDate(row.newestAcceptTime) },
        { label: "需求创建时间", render: ({ row }) => formatDetailDate(requirementValue(row).createdTime) }
    ],
    5: [
        { label: "编号", width: 80, render: ({ rowIndex }) => rowIndex + 1 },
        { label: "需求编号", render: ({ row }) => requirementLink(row) },
        {
            label: "运单编号",
            render: ({ row }) => {
                const id = transportOrderValue(row).id;
                return id ? (
                    <Link to={`/logistics/shippingNeed/waybillManagementInfo/${id}`}>{String(id)}</Link>
                ) : (
                    textValue(id)
                );
            }
        },
        { label: "生成运单时间", render: ({ row }) => formatDetailDate(transportOrderValue(row).createdTime) },
        {
            label: "运单当前状态",
            render: ({ row }) => enumValue(transportOrderValue(row).state, code => WAYBILL_STATE.getIntroByCode(code))
        },
        { label: "需求创建时间", render: ({ row }) => formatDetailDate(requirementValue(row).createdTime) }
    ]
};

/** 将旧 Vue 快捷时间范围转换为接口使用的整日时间边界。 */
const toScopeQuery = (scope: number): Pick<ShipNeedAnalysisQuery, "scope" | "beginTime" | "endTime"> => {
    const [beginDate, endDate] = calcRangeByScope(scope);
    return {
        scope,
        beginTime: dateFormat(beginDate, LOG_DATE_FORMAT_START),
        endTime: dateFormat(endDate, LOG_DATE_FORMAT_END)
    };
};

/** 船运需求转化分析最小可用页面，先覆盖统计卡片、状态明细和时间筛选。 */
export function ShipNeedAnalysisPage({
    loadCount = loadShipNeedAnalysisCount,
    loadDetails = loadShipNeedAnalysisDetails,
    loadExportDetails = loadShipNeedAnalysisDetails,
    downloadDetails,
    permissions
}: ShipNeedAnalysisPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 对齐旧 Vue v-auth，仅允许指定权限导出主明细。
    const canExport = (permissions ?? storePermissions).includes("ROLE_DEMAND_CONVERSION_EXPORT");
    const [scope, setScope] = useState<number | null>(3);
    const [customRange, setCustomRange] = useState<[string | null, string | null] | null>(null);
    const [activeStatus, setActiveStatus] = useState(1);
    const [detailPage, setDetailPage] = useState(1);
    const [detailPageSize, setDetailPageSize] = useState(10);
    const [count, setCount] = useState<ShipNeedAnalysisCount>({});
    const [rows, setRows] = useState<Record<string, unknown>[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const scopeQuery = useMemo(
        () =>
            customRange?.[0] && customRange[1]
                ? {
                      beginTime: dateFormat(customRange[0], LOG_DATE_FORMAT_START),
                      endTime: dateFormat(customRange[1], LOG_DATE_FORMAT_END)
                  }
                : toScopeQuery(scope ?? 3),
        [customRange, scope]
    );

    useEffect(() => {
        let disposed = false;
        const countQuery = scopeQuery;
        const detailQuery = { page: detailPage, size: detailPageSize, platConvertType: activeStatus, ...scopeQuery };
        void Promise.all([loadCount(countQuery), loadDetails(detailQuery)])
            .then(([countResult, detailResult]) => {
                if (disposed) return;
                setCount(countResult || {});
                setRows(detailResult.list || []);
                setTotal(detailResult.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setCount({});
                setRows([]);
                setTotal(0);
                setError("船运需求转化分析加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [activeStatus, detailPage, detailPageSize, loadCount, loadDetails, scopeQuery]);

    /** 切换状态后回到第一页，避免沿用上一状态的无效页码。 */
    const selectStatus = (status: number) => {
        setActiveStatus(status);
        setDetailPage(1);
    };

    /** 切换时间范围后回到第一页，确保新筛选结果从首屏开始展示。 */
    const selectScope = (nextScope: number) => {
        setCustomRange(null);
        setScope(nextScope);
        setDetailPage(1);
    };

    /** 使用自定义日期后清除快捷范围，并让统计与明细共享同一时间边界。 */
    const selectCustomRange = (_value: unknown, formattedValue: [string, string] | null) => {
        if (!formattedValue) return;
        // 先保存半选范围，适配日期控件连续选择开始/结束日期的受控输入行为。
        setCustomRange(formattedValue);
        if (!formattedValue[0] || !formattedValue[1]) return;
        setScope(null);
        setDetailPage(1);
    };

    /** 先按当前状态和时间范围取全量明细，确认后生成旧 Vue 兼容 Excel。 */
    const exportCurrentDetails = async () => {
        try {
            const result = await loadExportDetails({
                page: 1,
                size: 5000,
                platConvertType: activeStatus,
                ...scopeQuery
            });
            if (!result.list?.length) {
                setError("无数据导出");
                return;
            }
            if (!(await confirmDialog("是否导出当前数据？", "导出"))) return;
            const fileName = detailExportFileNames[activeStatus] || "需求明细";
            await (downloadDetails ?? ((rows, name) => downloadShipNeedDetails(rows, name, activeStatus)))(
                result.list,
                fileName
            );
        } catch {
            // 用户取消确认时保持页面状态，不额外提示错误。
        }
    };

    return (
        <section aria-label="ship-need-analysis-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <h2 className="m-0">船运需求转化分析</h2>
                <p>相关统计和明细数据跟随需求创建时间同步更新</p>
                {/* 顶部时间筛选复用旧 Vue 查询栏的日期范围宽度与短横线分隔符。 */}
                <Space wrap className="admin-table-filter-form">
                    <ZhRadioGroup
                        ariaLabel="统计范围"
                        options={[
                            { label: "今日", value: 1 },
                            { label: "近一周", value: 2 },
                            { label: "近一月", value: 3 },
                            { label: "近一季度", value: 4 },
                            { label: "近一年", value: 5 }
                        ]}
                        type="button"
                        value={scope}
                        // zhihao RadioGroup follows Ant Design's event signature; convert its string value to the numeric query scope.
                        onChange={event => selectScope(Number(event.target.value))}
                    />
                    <ZhDateRangePicker
                        aria-label="自定义需求创建时间"
                        value={customRange}
                        valueFormat="YYYY-MM-DD"
                        format="YYYY-MM-DD"
                        rangeSeparator="-"
                        startPlaceholder="开始日期"
                        endPlaceholder="结束日期"
                        onChange={selectCustomRange}
                    />
                </Space>
            </Card>
            <Card title="船运需求统计">
                {error ? <Alert role="alert" type="error" message={error} /> : null}
                <Space wrap>
                    <Button type="default" aria-label="需求总数">
                        需求总数：{count.totalNum || 0}
                    </Button>
                    {statuses.map(status => (
                        <Button
                            key={status.id}
                            type={activeStatus === status.id ? "primary" : "default"}
                            aria-label={status.label}
                            onClick={() => selectStatus(status.id)}
                        >
                            {status.label}：{count[status.field] || 0}
                        </Button>
                    ))}
                </Space>
            </Card>
            <Card title="船运需求转化分析">
                <ShipNeedConversionFunnel cardData={count} />
                <p className="mb-0">有效需求数：需求总数-待发布需求数-已取消需求数</p>
            </Card>
            <ShipNeedExposureAnalysis timeParams={scopeQuery} />
            <Card title="需求状态明细">
                <ZhTable<Record<string, unknown>>
                    rowKey={row => String((row.shippingRequirementPlat as { id?: unknown } | undefined)?.id || "row")}
                    data={rows}
                    pagination={false}
                    columns={detailColumnsByStatus[activeStatus] || detailColumnsByStatus[1]}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {canExport ? (
                                <Button
                                    type="primary"
                                    aria-label="导出需求状态明细"
                                    onClick={() => void exportCurrentDetails()}
                                >
                                    导出
                                </Button>
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={detailPage}
                                pageSize={detailPageSize}
                                total={total}
                                onChange={(page, pageSize) => {
                                    setDetailPageSize(pageSize);
                                    setDetailPage(pageSize !== detailPageSize ? 1 : page);
                                }}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
